import { sendTeamMessage } from "../../../api/client";
import { getUserFacingApiMessage } from "../../../utils/getUserFacingApiMessage";
import {
  buildPayloadFromAudioUrl,
  buildPayloadFromUploadedFile,
  uploadTeamFile,
} from "./teamAttachmentSend";
import { mergeMessages } from "./teamMessagesState";
import {
  buildOptimisticTeamMessage,
  createOptimisticTempId,
  getTeamCurrentUser,
  revokeOptimisticBlobUrl,
  stripOptimisticSendMarkers,
} from "./teamOptimisticMessage";

export async function prepareTeamSendPayload(payload = {}) {
  const { _file, _audioURL, ...rest } = payload;

  if (_file) {
    const uploaded = await uploadTeamFile(_file);
    return {
      ...buildPayloadFromUploadedFile(_file, uploaded.url),
      ...stripOptimisticSendMarkers(rest),
    };
  }

  if (_audioURL) {
    const audioPayload = await buildPayloadFromAudioUrl(_audioURL);
    return {
      ...audioPayload,
      ...stripOptimisticSendMarkers(rest),
    };
  }

  return stripOptimisticSendMarkers(rest);
}

export async function executeOptimisticTeamSend({
  spaceId,
  payload,
  setMessages,
  company,
  onInvalidateSpaces,
  syncLatest,
}) {
  const { currentUserId, authorName } = getTeamCurrentUser();
  const tempId = createOptimisticTempId();
  const optimistic = buildOptimisticTeamMessage(payload, {
    tempId,
    spaceId,
    currentUserId,
    authorName,
  });

  setMessages((prev) => mergeMessages(prev, [optimistic], "merge"));

  try {
    const apiPayload = await prepareTeamSendPayload(payload);
    const result = await sendTeamMessage({ ...apiPayload, spaceId }, company);
    const saved = result?.data || result;

    revokeOptimisticBlobUrl(optimistic);

    const confirmed = {
      ...optimistic,
      id: saved?.id ?? optimistic.id,
      createdAt: saved?.createdAt ?? optimistic.createdAt,
      content: apiPayload.content ?? optimistic.content,
      messageType: apiPayload.messageType ?? optimistic.messageType,
      imageUrl: apiPayload.imageUrl ?? optimistic.imageUrl,
      videoUrl: apiPayload.videoUrl ?? optimistic.videoUrl,
      audioUrl: apiPayload.audioUrl ?? optimistic.audioUrl,
      fileUrl: apiPayload.fileUrl ?? optimistic.fileUrl,
      fileName: apiPayload.fileName ?? optimistic.fileName,
      sendStatus: undefined,
      _optimistic: undefined,
      _localBlobUrl: undefined,
      errorMessage: undefined,
    };

    setMessages((prev) => {
      const withoutTemp = prev.filter((m) => m.id !== tempId);
      return mergeMessages(withoutTemp, [confirmed], "merge");
    });

    onInvalidateSpaces?.();
    syncLatest?.().catch(() => {});

    return saved;
  } catch (err) {
    const { message, action } = getUserFacingApiMessage(err);
    const errorMessage = [message || "Falha ao enviar.", action].filter(Boolean).join(" ");
    setMessages((prev) =>
      prev.map((m) =>
        m.id === tempId
          ? {
              ...m,
              sendStatus: "error",
              errorMessage,
            }
          : m
      )
    );
    throw err;
  }
}
